

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Card, Group, Text, ActionIcon } from '@mantine/core';
import { IconGripVertical, IconEdit, IconTrash } from '@tabler/icons-react';
import { PageElement } from '@holloway/dsl';
import { getPageElementInfo } from '@/features/editor/pageElements';

interface Props {
    element: PageElement;
    isSelected: boolean;
    onSelect: () => void;
    onRemove: () => void;
    children: React.ReactNode;
}

export default function DraggablePageElement({ element, isSelected, onSelect, onRemove, children }: Props) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: element.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 1,
        position: 'relative' as const,
    };

    const elementInfo = getPageElementInfo(element.type);

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <Card
                role="group"
                aria-label={`${elementInfo?.label || element.type} element`}
                padding="sm"
                withBorder
                style={{
                    cursor: 'default',
                    border: isSelected ? '2px solid #415A91' : '1px solid #E4E7ED',
                    backgroundColor: isSelected ? '#E8EDF5' : 'white',
                    borderRadius: 10,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    onSelect();
                }}
            >
                <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                        <div {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                            <IconGripVertical size={16} style={{ color: '#8E99AB' }} />
                        </div>
                        <Text size="xs" fw={600} tt="uppercase" c="#415A91" style={{ letterSpacing: '0.03em' }}>
                            {elementInfo?.label || element.type}
                        </Text>
                    </Group>

                    <Group gap={4}>
                        <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            aria-label={`Remove ${elementInfo?.label || element.type}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove();
                            }}
                        >
                            <IconTrash size={14} />
                        </ActionIcon>
                    </Group>
                </Group>

                <Box pl={28}>
                    {children}
                </Box>
            </Card>
        </div>
    );
}
